import isOnlineStatus from "is-online";
import { forkJoin, from, Observable, of, zip } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { FormatDate } from "../../_objects/format-date";
import { InspectionListItem } from "../../_objects/inspection";
import { InspectionImage, Metadata } from "../../_objects/inspection-image";
import {
  IndividualReportForOffline,
  InspectionReport,
  InspectionReportMetaData,
  InspectionReportList,
  InspectionReportListItem,
  InspectionReportNew,
  InspectionReportPdfUrl,
  InspectionReportPermission,
  InspectionReportReuse,
  InspectionReportUpdate,
  InspectionReportUpdateLocations,
  InspectionReportUpdateTexts,
  InspectionReportUri,
  PastReportVersion,
  PdfOptions,
  ReportStatus
} from "../../_objects/inspection-report";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";
import { DataProperty, FILE_SYSTEM } from "../../_offline/electron.constants";
import { IndividualReportTemplateSchema } from "../../_offline/models/individual-report-template.schema";
import { IndividualReportSchema } from "../../_offline/models/individual-report.schema";
import { InspectionSchema, InspectionSchemaStatusUpdate } from "../../_offline/models/inspection.schema";
import { InspectionImageSchema } from "../../_offline/models/inspectionImage.schema";
import { PreviewImageDetail } from "../../_offline/models/report-preview/preview-image-detail";
import { ReportDataGenerator } from "../../_offline/models/report-preview/report-data-generator";
import { ReportTitleFormat, TemplateDefinition, TemplatePageSection } from "../../_offline/models/report-preview/report-template";
import { TableData } from "../../_offline/models/report-preview/TableData";
import { ViewDataModel } from "../../_offline/models/report-preview/view-data-model";
import { TenantDetailSchema } from "../../_offline/models/tenant-detail.schema";
import { TenantConfigSchema } from "../../_offline/models/tenantConfiguration.schema";
import { UploadDataType, UploadManagementSchema } from "../../_offline/models/upload-management.schema";
import { UserSchema } from "../../_offline/models/user.schema";
import { GUID, INSPECTION_REPORT_STATUS_CODE, INSPECTION_STATUS_CODE, MEASUREMENT_IMAGE_TYPE, TenantConfig } from "../../app.constants";
import { MeasurementModeName } from "../../_objects/measurement-mode-name";
import { UtilsService } from "../_utils.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { IndividualReportTemplateDbService } from "../database/individual-report-template-db.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { InspectionImageDbService } from "../database/inspection-image-db.service";
import { TenantConfigurationDbService } from "../database/tenant-configuration-db.service";
import { TenantDetailDbService } from "../database/tenant-detail-db.service";
import { UploadManagementDbService } from "../database/upload-management-db.service";
import { UserDbService } from "../database/user-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { InspectionReportContractService } from "../interfaces/inspection-report-contract.service";
import { NetworkStatusService } from "../network-status.service";
import { OfflineManagementService } from "../offline-management.service";
import { InspectionReportOnlineService } from "../online/inspection-report-online.service";
import { ReportPreviewContentService } from "../report-preview-content.service";
import { SubjectFilter } from "../storage.service";
import { LoginUserOfflineService } from "./login-user-offline.service";
import { TextComponent } from "../../_offline/models/report-preview/report-text";
import { FeatureFlagOfflineService } from "../feature-flag/feature-flag.offline.service";
import { SubjectSchema } from "../../_offline/models/subject.schema";

class ReportLocation {
  identifier: string;
  imageIds: string[];
  constructor(identifier: string, imageIds: string[]) {
    this.identifier = identifier;
    this.imageIds = imageIds;
  }
}

@Injectable({
  providedIn: "root"
})
export class InspectionReportOfflineService implements InspectionReportContractService {
  readonly dynamic = "dynamic";
  readonly dynamicEditable = "dynamic-editable";
  readonly defaultDateFormat = "yyyy/MM/dd";

  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  get tenantId() {
    return this.loginUserService.tenantId;
  }

  private get isAppOnline() {
    return this.networkStatusService.isOnline;
  }

  constructor(
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private individualReportDbService: IndividualReportDbService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private userDbService: UserDbService,
    private tenantDbService: TenantDetailDbService,
    private configDbService: TenantConfigurationDbService,
    private loginUserService: LoginUserOfflineService,
    private uploadManagementDbService: UploadManagementDbService,
    private fileSysAccessService: FileSystemAccessService,
    private reportPreviewService: ReportPreviewContentService,
    private networkStatusService: NetworkStatusService,
    private inspectionReportOnlineService: InspectionReportOnlineService,
    private offlineManagementService: OfflineManagementService,
    private translate: TranslateService,
    private utils: UtilsService,
    private featureFlagService: FeatureFlagOfflineService
  ) {}

  // RP01 GET
  getInspectionReports(
    inspectionId?: string,
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string },
    page?: { index: number; size: number },
    searchText?: string
  ): Observable<InspectionReportList> {
    if (page) {
      if (!page.index) {
        page.index = 1;
      }
      if (!page.size) {
        page.size = 10;
      }
    } else {
      page = { index: 1, size: 10 };
    }
    // 取得範囲の算出
    const start = (page.index - 1) * page.size;
    const end = page.index * page.size;
    let reports: IndividualReportSchema[];
    let blobPath: string;
    return forkJoin({
      reportSchemas: this.individualReportDbService.getAll(inspectionId, statusCode, subjectFilter, isOwn, sort, true),
      blobPath: from(this.fileSysAccessService.getSaveBlobFolderPath())
    })
      .pipe(
        switchMap((x) => {
          reports = x.reportSchemas;
          blobPath = x.blobPath;
          const inspectionIds = reports.map((r) => r.inspectionId);
          return this.inspectionDbService.getListById(inspectionIds, true);
        })
      )
      .pipe(
        map((inspections) => {
          const result = new InspectionReportList();
          const filteredInsp = this.applySubjectFilter(inspections, subjectFilter);

          // inspectionIdと一致するレポートを取得
          const matchedReports = reports
            .filter((report) => filteredInsp.some((insp) => insp.id === report.inspectionId))
            .filter((report) => !searchText || report.title.toLowerCase().includes(searchText.toLowerCase()));
          const items = matchedReports
            .map((report) => {
              return this.genInspectionReportListItem(report, filteredInsp, blobPath);
            })
            .sort((a, b) => {
              if (!sort?.active || !sort?.direction) {
                return 0;
              }

              // 配列のソート
              let genreA;
              let genreB;

              if (sort.active === "subjectModelName") {
                genreA = a.inspection.subjectModelName.toUpperCase();
                genreB = b.inspection.subjectModelName.toUpperCase();
              } else {
                genreA = a[sort.active].toUpperCase();
                genreB = b[sort.active].toUpperCase();
              }

              let comparison = 0;
              if (genreA > genreB) {
                comparison = 1;
              } else if (genreA < genreB) {
                comparison = -1;
              }
              return sort.direction.toUpperCase() === "ASC" ? comparison : comparison * -1;
            })
            .slice(start, end); // ページ表示分切り出し

          result.items = items;
          result.page = page.index;
          result.total = matchedReports.length;
          return result;
        })
      );
  }

  private applySubjectFilter(inspections: InspectionSchema[], subjectFilter?: SubjectFilter): InspectionSchema[] {
    if (!subjectFilter) return inspections;

    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return inspections.filter((insp) => {
        const subject = insp.subject as SubjectSchema | undefined;
        if (!subject) return false;
        if (subjectFilter.subjectId && insp.subjectId !== subjectFilter.subjectId) return false;
        if (subjectFilter.subjectModelId) {
          if (subject.id !== subjectFilter.subjectModelId && subject.assetTypeId !== subjectFilter.subjectModelId) {
            return false;
          }
        }
        return true;
      });
    }

    return inspections
      .filter((insp) => !subjectFilter.subjectId || insp.subjectId === subjectFilter.subjectId)
      .filter((insp) => !subjectFilter.subjectModelId || insp.subject?.subjectModelId === subjectFilter.subjectModelId)
      .filter((insp) => !subjectFilter.owner || insp.subject?.owner === subjectFilter.owner)
      .filter((insp) => !subjectFilter.country || insp.subject?.country === subjectFilter.country)
      .filter((insp) => !subjectFilter.district || insp.subject?.district === subjectFilter.district)
      .filter((insp) => !subjectFilter.state || insp.subject?.state === subjectFilter.state)
      .filter((insp) => !subjectFilter.site || insp.subject?.site === subjectFilter.site)
      .filter((insp) => !subjectFilter.project || insp.subject?.project === subjectFilter.project);
  }

  // RP02 GET
  getInspectionReport(reportId: string, version: number = null): Observable<InspectionReport> {
    return forkJoin({
      schema: this.individualReportDbService.get(reportId, version),
      blobPath: from(this.fileSysAccessService.getSaveBlobFolderPath())
    }).pipe(map((x) => this.genReportFromSchema(x.schema, x.blobPath)));
  }

  // RP03 POST
  createInspectionReport(inspectionReport: InspectionReportNew): Observable<any> {
    let blobPath = "";
    return zip(
      this.individualReportTemplateDbService.get(inspectionReport.templateId),
      this.configDbService.getAll(),
      this.inspectionDbService.getInspection(inspectionReport.inspectionIds[0], true),
      this.inspectionImageDbService.getAll(inspectionReport.inspectionIds[0]),
      this.userDbService.getAll(),
      this.tenantDbService.get(),
      from(this.fileSysAccessService.getSaveBlobFolderPath())
    )
      .pipe(
        switchMap((values) => {
          const template = values[0];
          const configs = values[1];
          const inspection = values[2];
          const images = values[3];
          const users = values[4];
          const tenantDetail = values[5];
          blobPath = values[6];
          const schema = this.genInspectionReportSchema(inspectionReport, template, configs, inspection, images, users, tenantDetail);
          const uploadManageSchema = new UploadManagementSchema(
            schema.id,
            this.tenantId,
            UploadDataType.IndividualReport,
            true,
            this.userId,
            inspectionReport.inspectionIds[0]
          );
          return zip(this.individualReportDbService.put(schema), this.uploadManagementDbService.put(uploadManageSchema));
        })
      )
      .pipe(
        switchMap((values) => {
          const schema = values[0];
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              const measurementModeName = new MeasurementModeName(this.translate);
              const reportInfo = new IndividualReportForOffline(schema);
              return this.offlineManagementService.putOfflineIndividual(reportInfo.reportId, reportInfo, measurementModeName).pipe(
                switchMap((report) => {
                  schema.lastUpdateDate = report.lastUpdateDate;
                  return this.individualReportDbService.put(schema);
                })
              );
            })
          );
        })
      )
      .pipe(map((schema) => this.genReportFromSchema(schema, blobPath)));
  }

  // RP04 PUT
  updateInspectionReport(reportId: string, inspectionReportUpdate: InspectionReportUpdate): Observable<InspectionReport> {
    const blobPath = "";
    return zip(
      this.individualReportDbService.get(reportId),
      this.individualReportTemplateDbService.get(inspectionReportUpdate.templateId),
      this.inspectionDbService.getInspection(inspectionReportUpdate.inspectionIds[0], true),
      this.userDbService.getAll(),
      this.tenantDbService.get(),
      from(this.fileSysAccessService.getSaveBlobFolderPath())
    )
      .pipe(
        switchMap((values) => {
          const reportSchema = values[0];
          if (reportSchema.reportId !== inspectionReportUpdate.reportId) {
            return of(reportSchema);
          }

          const template = values[1];
          const inspection = values[2];
          const users = values[3];
          const tenantDetail = values[4];
          const currentUser = users.find((user) => user.id === this.userId);

          reportSchema.templateId = inspectionReportUpdate.templateId;
          reportSchema.inspectionId = inspectionReportUpdate.inspectionIds[0];
          reportSchema.reportLocations = JSON.stringify(inspectionReportUpdate.reportLocations);
          reportSchema.editableTexts = JSON.stringify(inspectionReportUpdate.editableTexts);
          reportSchema.status = inspectionReportUpdate.status;
          reportSchema.isAutoInputTitle = inspectionReportUpdate.isAutoInputTitle;
          reportSchema.schemaVersion = inspectionReportUpdate.schemaVersion;
          reportSchema.draftReportQuality = inspectionReportUpdate.draftQuality;
          reportSchema.finalReportQuality = inspectionReportUpdate.finalQuality;

          reportSchema.title = this.genReportTitle(
            inspectionReportUpdate.title,
            template,
            inspectionReportUpdate.isAutoInputTitle,
            inspection,
            reportSchema,
            currentUser,
            users,
            tenantDetail
          );
          return this.individualReportDbService.put(reportSchema);
        })
      )
      .pipe(
        switchMap((schema) => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionReportOnlineService.updateInspectionReport(reportId, inspectionReportUpdate).pipe(
                switchMap((report) => {
                  schema.lastUpdateDate = report.lastUpdateDate;
                  return this.individualReportDbService.put(schema);
                })
              );
            })
          );
        })
      )
      .pipe(map((schema) => this.genReportFromSchema(schema, blobPath)));
  }

  // RP05 DELETE
  deleteInspectionReport(reportId: string): Observable<any> {
    return this.individualReportDbService
      .delete(reportId)
      .pipe(
        switchMap((ids) => {
          const obs = ids.map((id) => this.uploadManagementDbService.deleted(id, UploadDataType.IndividualReport));
          return zip(...obs);
        })
      )
      .pipe(
        switchMap((schema) => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionReportOnlineService
                .deleteInspectionReport(reportId)
                .pipe(switchMap(() => this.individualReportDbService.delete(reportId, false)));
            })
          );
        })
      );
  }

  // RP06 GET
  getInspectionReportsByInspectionIds(inspectionIds: string[], size: number, index: number = 1): Observable<InspectionReportList> {
    // 取得範囲の算出
    const start = (index - 1) * size;
    const end = index * size;

    return forkJoin({
      reportSchemas: this.individualReportDbService.getByInspectionIds(inspectionIds),
      blobPath: from(this.fileSysAccessService.getSaveBlobFolderPath())
    }).pipe(
      map((x) => {
        const result = new InspectionReportList();
        if (x.reportSchemas.length < 1) {
          result.items = [];
          result.total = 0;
          result.page = index;
          return result;
        }

        const newestList = x.reportSchemas.filter((report) => {
          const relatedReports = x.reportSchemas.filter((r) => report.reportId === r.reportId);
          return !relatedReports.some((r) => r.version > report.version);
        });
        const sliced = newestList.slice(start, end);

        result.items = sliced.map((report) => this.genInspectionReportListItem(report, [], x.blobPath));
        result.total = x.reportSchemas.length;
        result.page = index;
        return result;
      })
    );
  }

  // RP07 PUT
  patchInspectionReportLocations(inspectionReportUpdateLocations: InspectionReportUpdateLocations): Observable<any> {
    let blobPath: string;
    return forkJoin({
      schema: this.individualReportDbService.get(inspectionReportUpdateLocations.reportId),
      blobPath: from(this.fileSysAccessService.getSaveBlobFolderPath())
    })
      .pipe(
        switchMap((x) => {
          x.schema.reportLocations = JSON.stringify(inspectionReportUpdateLocations.reportLocations);
          blobPath = x.blobPath;
          return this.individualReportDbService.put(x.schema);
        })
      )
      .pipe(
        switchMap((schema) => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionReportOnlineService.patchInspectionReportLocations(inspectionReportUpdateLocations).pipe(
                switchMap((report) => {
                  schema.lastUpdateDate = report.lastUpdateDate;
                  return this.individualReportDbService.put(schema);
                })
              );
            })
          );
        })
      )
      .pipe(map((schema) => this.genReportFromSchema(schema, blobPath)));
  }

  // RP08 PUT
  patchInspectionReportTexts(inspectionReportUpdateTexts: InspectionReportUpdateTexts): Observable<any> {
    let blobPath: string;
    return forkJoin({
      schema: this.individualReportDbService.get(inspectionReportUpdateTexts.reportId),
      blobPath: from(this.fileSysAccessService.getSaveBlobFolderPath())
    })
      .pipe(
        switchMap((x) => {
          x.schema.editableTexts = JSON.stringify(inspectionReportUpdateTexts.editableTexts);
          x.schema.excludedInputGroups = JSON.stringify(inspectionReportUpdateTexts.excludedInputGroups);
          blobPath = x.blobPath;
          return this.individualReportDbService.put(x.schema);
        })
      )
      .pipe(
        switchMap((schema) => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionReportOnlineService.patchInspectionReportTexts(inspectionReportUpdateTexts).pipe(
                switchMap((report) => {
                  schema.lastUpdateDate = report.lastUpdateDate;
                  return this.individualReportDbService.put(schema);
                })
              );
            })
          );
        })
      )
      .pipe(map((schema) => this.genReportFromSchema(schema, blobPath)));
  }

  // RP09 PUT
  patchInspectionReportStatus(reportStatus: ReportStatus): Observable<InspectionReport> {
    return of();
  }

  // RP10 PUT
  importInspectionReportTexts(inspectionReport: InspectionReport, file: File, fileType: string): Observable<InspectionReport> {
    return of();
  }

  // RP11 GET
  getInspectionReportPdf(reportId: string, version?: number, preview?: boolean): Observable<Blob> {
    return this.individualReportDbService.get(reportId, version).pipe(
      switchMap((report) => {
        const reportFileName = this.getReportFileName(report, preview);
        const filePath = `inspections\\${report.inspectionId}\\reports\\${report.reportId}\\${reportFileName}`;
        const mimeType = "application/pdf";

        return from(this.fileSysAccessService.getFile(filePath, report.title, mimeType));
      })
    );
  }

  private getReportFileName(report: IndividualReportSchema, isPreview: boolean = false): string {
    if (isPreview) {
      return `draft-${report.version}.pdf`;
    }
    const normalizedTitle = (report.finalReportTitle ?? "").trim();
    if (normalizedTitle && normalizedTitle.toLowerCase() !== "null" && normalizedTitle.toLowerCase() !== "undefined") {
      return `${normalizedTitle}.pdf`;
    }
    return `report-${report.version}.pdf`;
  }

  // RP12 GET
  getInspectionReportPdfUrl(reportId: string, version?: string, preview?: boolean): Observable<InspectionReportPdfUrl> {
    const versionNum = Number(version);
    return zip(this.individualReportDbService.get(reportId, versionNum), from(this.fileSysAccessService.getSaveBlobFolderPath())).pipe(
      switchMap((values) => {
        const report = values[0];
        const blobPath = values[1];
        const result = new InspectionReportPdfUrl();
        const fileName = this.getReportFileName(report, preview);
        result.url = FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + `inspections\\${report.inspectionId}\\reports\\${report.reportId}\\${fileName}`;
        return of(result);
      })
    );
  }

  // RP13 POST
  createInspectionReportPdf(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean
  ): Observable<InspectionReportPdfUrl> {
    return of();
  }

  // RP14 PUT
  putInspectionReportReuse(reuseRequest: InspectionReportReuse): Observable<InspectionReport> {
    return of();
  }

  // RP15 GET
  getInspectionReportSharedLink(reportId: string, expiryDate: string, version?: number, isPreview?: boolean): Observable<InspectionReportUri> {
    return of();
  }

  // RP015 POST
  createNewVersionInspectionReport(reportId: string): Observable<any> {
    return this.individualReportDbService
      .get(reportId)
      .pipe(
        switchMap((schema) => {
          const oldVersionReportSchema = JSON.parse(JSON.stringify(schema));
          oldVersionReportSchema.isNewestVersion = false;

          const date = new FormatDate();
          const newVersionReportSchema = JSON.parse(JSON.stringify(schema));
          newVersionReportSchema.status = INSPECTION_REPORT_STATUS_CODE.EDITING;
          newVersionReportSchema.version = schema.version + 1;
          newVersionReportSchema.id = `${newVersionReportSchema.reportId}_${newVersionReportSchema.version}`;
          newVersionReportSchema.lastUpdatedBy = this.userId;
          newVersionReportSchema.lastUpdateDate = date.toString();
          return zip(
            this.individualReportDbService.put(newVersionReportSchema),
            this.individualReportDbService.put(oldVersionReportSchema),
            from(this.fileSysAccessService.getSaveBlobFolderPath())
          );
        })
      )
      .pipe(map((values) => this.genReportFromSchema(values[0], values[2])));
  }

  // RP16 GET
  getInspectionReportVersion(reportId: string): Observable<PastReportVersion[]> {
    return this.individualReportDbService.getAllVersions(reportId).pipe(
      map((reportSchemas) => {
        return reportSchemas.map((schema) => {
          const result = new PastReportVersion();
          result.title = schema.title;
          result.status = schema.status;
          result.version = schema.version;
          result.reportId = schema.reportId;
          return result;
        });
      })
    );
  }

  // RP17 POST
  createReportPdfAsync(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean
  ): Observable<InspectionReport> {
    const blobPath = "";
    return this.individualReportDbService.get(reportId).pipe(
      switchMap((reportSchema) => {
        return forkJoin([
          of(reportSchema),
          this.inspectionDbService.getInspection(reportSchema.inspectionId, true),
          from(this.fileSysAccessService.getSaveBlobFolderPath())
        ]);
      }),
      switchMap(([reportSchema, inspectionSchema, blobPath]) => {
        reportSchema.status = INSPECTION_REPORT_STATUS_CODE.COMPLETED;
        inspectionSchema.status = INSPECTION_STATUS_CODE.COMPLETED;

        reportSchema.finalReportTitle = InspectionReport.getFinalReportTitle(reportSchema.title, reportSchema.version);

        return forkJoin([
          this.individualReportDbService.put(reportSchema),
          this.inspectionDbService.updateInspectionStatus(this.genInspectionStatusSchema(inspectionSchema))
        ]).pipe(
          catchError((error) => {
            console.error(error);
            throw error;
          })
        );
      }),
      map(([reportSchema, _]) => this.genReportFromSchema(reportSchema, blobPath))
    );
  }

  private genInspectionStatusSchema(inspection: InspectionSchema) {
    const schema = new InspectionSchemaStatusUpdate();
    const date = new FormatDate();
    schema.id = inspection.id;
    schema.status = inspection.status === INSPECTION_STATUS_CODE.NOTINSPECTED ? INSPECTION_STATUS_CODE.EDITING : inspection.status;
    schema.lastUpdatedBy = inspection.lastUpdatedBy;
    schema.lastUpdateDate = date.toString();

    return schema;
  }

  createReportThumbnail(reportId: string, pdfOptions: PdfOptions, measurementModeName: MeasurementModeName): Observable<any> {
    return of();
  }

  // RP19 PUT
  setUnavailableThumbFilePath(reportId: string): Observable<any> {
    return of();
  }

  // RP20 POST /api/reports/reports/metadata
  getReportsMetaDataForInspections(inspectionIds: string[]): Observable<InspectionReportMetaData[]> {
    const getReportMetaData = (inspectionId: string) =>
      this.individualReportDbService.getReportsCountDetail(inspectionId).pipe(
        map((schemas) => ({
          inspectionId: inspectionId,
          reportCount: schemas.length,
          reportDetails: schemas.map((x) => ({ title: x.title, lastUpdateDate: x.lastUpdateDate, status: x.status, reportId: x.reportId }))
        }))
      );

    return forkJoin(inspectionIds.map(getReportMetaData));
  }

  // RPH1 GET
  getInspectionReportPreview(reportId: string, measurementModeName: MeasurementModeName, firstPageOnly: boolean): Observable<string> {
    return of();
  }

  private genInspectionReportSchema(
    inspectionReport: InspectionReportNew,
    reportTemplate: IndividualReportTemplateSchema,
    configs: TenantConfigSchema[],
    inspection: InspectionSchema,
    images: InspectionImageSchema[],
    users: UserSchema[],
    tenantDetail: TenantDetailSchema
  ) {
    const schema = new IndividualReportSchema();
    const tempId = GUID.generateUUID();
    const currentUser = users.find((user) => user.id === this.userId);
    schema.reportId = tempId;
    schema.version = 1;
    schema.isNewestVersion = true;
    schema.id = `${schema.reportId}_${schema.version}`;
    schema.inspectionId = inspectionReport.inspectionIds[0];
    schema.manageUserId = this.userId;

    schema.status = INSPECTION_REPORT_STATUS_CODE.EDITING;
    schema.templateId = inspectionReport.templateId;
    schema.blobStorageAccount = null;
    schema.schemaVersion = inspectionReport.schemaVersion;
    schema.type = "individual";
    schema.rowVersion = null;
    schema.isAutoInputTitle = inspectionReport.isAutoInputTitle;
    schema.isValid = true;
    schema.isDeleted = false;

    const date = new FormatDate();
    schema.createdBy = this.userId;
    schema.lastUpdatedBy = this.userId;
    schema.createDate = date.toString();
    schema.lastUpdateDate = date.toString();
    schema.tenantId = this.tenantId;

    schema.reportLocations = JSON.stringify([]);
    // IsSelectedAllFolderWhenCreatingReport=trueの時、被検体モデルの部位を全てレポートに含める。
    const isSelectedAllFolder = configs.find((c) => c.key === TenantConfig.IsSelectedAllFolderWhenCreatingReport.KEY);
    if (isSelectedAllFolder?.value === "true") {
      const subjectModelLocations = inspection.subject.subjectModel.subjectModelLocations;
      const subjectModelLocation = subjectModelLocations.find((_) => _.version === inspection.locationVersion);
      const locations = JSON.parse(subjectModelLocation.locations) as SubjectLocation[];
      const json = locations.map((loc) => new ReportLocation(loc.identifier, []));
      schema.reportLocations = JSON.stringify(json);
    }

    // ImageSelectModeWhenCreatingReport=TrueまたはOptionalかつレポート生成時の選択肢で含めるを選択した場合,
    // 検査に紐づく画像を全てレポートに含める。
    const imageSelectMode = configs.find((c) => c.key === TenantConfig.ImageSelectModeWhenCreatingReport.KEY);
    if (imageSelectMode?.value === "1" || (imageSelectMode?.value === "2" && inspectionReport.isSelectedImage)) {
      const locations = schema.reportLocations ? (JSON.parse(schema.reportLocations) as ReportLocation[]) : ([] as ReportLocation[]);

      images.forEach((image) => {
        if (image.subjectLocationId && this.isImage(image.imageType)) {
          const targetLocation = locations.find((loc) => loc.identifier === image.subjectLocationId);
          if (targetLocation) {
            targetLocation.imageIds.push(image.id);
          } else {
            const reportLocation = new ReportLocation(image.subjectLocationId, [image.id]);
            locations.push(reportLocation);
          }
        }
      });
      schema.reportLocations = JSON.stringify(locations);
    }

    schema.editableTexts = JSON.stringify({});
    // EditableId Templateの「dynamic-editable」のeditableIdとプレビュー表示される値のデフォルト値を保存。
    const viewData = this.GetViewDataAsync(reportTemplate, configs, inspection, images, schema, users, tenantDetail);

    const editableTexts = this.reportPreviewService.getDynamicEditableTexts(
      reportTemplate.template,
      schema,
      inspection,
      currentUser,
      users,
      tenantDetail.name,
      viewData
    );
    schema.editableTexts = JSON.stringify(editableTexts);

    schema.title = this.genReportTitle(
      inspectionReport.title,
      reportTemplate,
      inspectionReport.isAutoInputTitle,
      inspection,
      schema,
      currentUser,
      users,
      tenantDetail
    );

    return schema;
  }

  private GetViewDataAsync(
    reportTemplate: IndividualReportTemplateSchema,
    configs: TenantConfigSchema[],
    inspectionSchema: InspectionSchema,
    inspectionImageSchemas: InspectionImageSchema[],
    reportSchema: IndividualReportSchema,
    users: UserSchema[],
    tenantDetail: TenantDetailSchema
  ) {
    const template = JSON.parse(reportTemplate.template) as TemplateDefinition;
    const result = new ViewDataModel();
    const summary = template.bodies?.find((body) => body.inspectionSummary != null);
    if (summary === null) return result;

    const images = [];
    inspectionImageSchemas.forEach((image) => {
      images.push(this.genInspectionImage(image));
    });

    let previewImages = [];

    const reportLocations = reportSchema.reportLocations ? (JSON.parse(reportSchema.reportLocations) as ReportLocation[]) : ([] as ReportLocation[]);
    const subjectModel = new SubjectModel();
    subjectModel.setSubjectModelSchema(inspectionSchema.subject.subjectModel);
    const locList = this.utils.getLocations(subjectModel);

    images.forEach((image) => {
      const schema = inspectionImageSchemas.find((imgSchema) => imgSchema.id === image.id);
      if (schema) {
        const measurementItems = schema.measurementItems ? JSON.parse(schema.measurementItems) : [];
        const result = new PreviewImageDetail(image, schema.width, schema.height, false, schema.tenantId, measurementItems);
        previewImages.push(result);
      }
    });

    reportLocations.forEach((location) => {
      if (location.imageIds.length < 1 && locList.some((loc) => loc.identifier === location.identifier)) {
        const dummy = new PreviewImageDetail();
        dummy.id = this.generateUUID();
        dummy.inspectionId = inspectionSchema.id;
        dummy.subjectLocationId = inspectionSchema.id;
        dummy.createdBy = this.loginUserService.loginUser.id;
        dummy.thumbFilePath = "";
        dummy.setExtendProp(85, 68, true, this.tenantId, []);
        dummy.isDummy = true;
        previewImages.push(dummy);
        location.imageIds.push(dummy.id);
      }
    });

    this.reportPreviewService.reportLocationsWithDummy = reportLocations;
    if (previewImages.length > 0) {
      previewImages = previewImages.sort((a, b) => this.sortPreviewImage(a, b));
    }

    const inspectionSummary = this.calcSummaryPage(previewImages, reportLocations, subjectModel, locList, summary);

    inspectionSummary?.forEach((summaryTable) => {
      summaryTable.forEach((tableData) => {
        const image = previewImages.find((_) => tableData.images.some((image) => image.id === _.id));
        result.summaryData.push({
          tableData: tableData,
          imageId: image ? (image.isDummy ? "" : image?.id) : "",
          fileName: image?.fileName
        });
        result.isAllDataMode = summary.inspectionSummary?.table?.dataMode === DataProperty.All;
      });
    });

    return result;
  }

  // Title: Template.ReportTitleInputMode=trueまたはOptionalかつ
  // レポート生成時の選択肢で自動補完を選択した場合 Template.ReportTitleFormatから生成する
  private genReportTitle(
    title: string,
    reportTemplate: IndividualReportTemplateSchema,
    isAutoInputTitle: boolean,
    inspection: InspectionSchema,
    report: IndividualReportSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantDetail: TenantDetailSchema
  ) {
    if (reportTemplate.reportTitleInputMode === "1" || (reportTemplate.reportTitleInputMode === "2" && isAutoInputTitle)) {
      title = "";
      const reportTitleFormat = JSON.parse(reportTemplate.reportTitleFormat) as ReportTitleFormat[];
      if (reportTitleFormat !== null || reportTitleFormat.length > 0) {
        const dynamicTexts = this.reportPreviewService.getTexts(reportTemplate.template, this.dynamic);
        const editableTexts = this.reportPreviewService.getTexts(reportTemplate.template, this.dynamicEditable);

        reportTitleFormat.forEach((format) => {
          let titlePart = "";
          switch (format.type) {
            case "static":
              titlePart = format.value;
              break;

            case "dynamic":
              let textComponent = dynamicTexts.find((t) => t.dynamicId === format.value);
              if (textComponent === null || textComponent === undefined) {
                textComponent = new TextComponent(format.value, format.value);
              }

              titlePart = this.reportPreviewService.getContent(
                textComponent.dynamicId,
                textComponent.text,
                format.format,
                report,
                inspection,
                currentUser,
                users,
                tenantDetail.name
              );
              break;

            case "dynamic-editable":
              let textArea = editableTexts.find((t) => t.dynamicId === format.value);
              if (textArea == null || textArea === undefined) {
                textArea = new TextComponent(format.value, format.value, format.value);
              }
              titlePart = this.reportPreviewService.getEditableContent(
                textArea,
                format.format,
                report,
                inspection,
                currentUser,
                users,
                tenantDetail.name
              );
              break;

            default:
              break;
          }

          title += titlePart !== "" && titlePart !== null ? titlePart : format.value;
        });
      }
    }

    return title;
  }
  private getPermission(report: IndividualReportSchema) {
    let permission = InspectionReportPermission.Read;

    if (this.loginUserService.isAdminRole() || this.loginUserService.loginUser.id === report.createdBy) {
      permission += InspectionReportPermission.Write;
    }

    return permission;
  }

  private genReportFromSchema(schema: IndividualReportSchema, blobPath: string) {
    const result = new InspectionReport();
    result.id = schema.id;
    result.reportId = schema.reportId;
    result.title = schema.title;
    result.inspectionIds = [schema.inspectionId];
    result.templateId = schema.templateId;
    result.version = schema.version;
    result.status = schema.status;
    result.reportLocations = JSON.parse(schema.reportLocations) as ReportLocation[];
    result.editableTexts = JSON.parse(schema.editableTexts);
    result.excludedInputGroups = JSON.parse(schema.excludedInputGroups);
    result.permission = this.getPermission(schema);
    result.isAutoInputTitle = schema.isAutoInputTitle;
    result.schemaVersion = schema.schemaVersion;
    result.createdBy = schema.createdBy;
    result.createDate = schema.createDate;
    result.lastUpdatedBy = schema.lastUpdatedBy;
    result.lastUpdateDate = schema.lastUpdateDate;
    result.draftQuality = schema.draftReportQuality;
    result.finalQuality = schema.finalReportQuality;
    result.thumbFilePath =
      schema.thumbFilePath === null || schema.thumbFilePath === "" ? null : blobPath + schema.thumbFilePath + this.genCacheParam();
    return result;
  }

  private genInspectionReportListItem(schema: IndividualReportSchema, inspectionSchemas: InspectionSchema[], blobPath: string) {
    const result = new InspectionReportListItem();
    result.reportId = schema.reportId;
    result.title = schema.title;
    result.status = schema.status;
    result.permission = this.getPermission(schema);
    result.lastUpdateDate = schema.lastUpdateDate;
    result.inspection = this.genInspections(schema.inspectionId, inspectionSchemas)[0];
    result.createdBy = schema.createdBy;
    result.thumbFilePath =
      schema.thumbFilePath === null || schema.thumbFilePath === "" ? null : blobPath + schema.thumbFilePath + this.genCacheParam();
    return result;
  }

  private genInspections(inspectionId: string, inspectionSchemas: InspectionSchema[]) {
    return inspectionSchemas
      .filter((inspSchema) => inspSchema.id === inspectionId)
      .map((inspSchema) => {
        const result = new InspectionListItem();
        result.setInspectionSchema(inspSchema);
        return result;
      });
  }

  // TODO 一時的にid付与のためのコード。別途削除
  /**
   * UUIDを生成する
   * 流用ロジック：https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
   * ライセンス  ：Apache License v2.0
   */
  private generateUUID(): string {
    const chars = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".split("");
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case "x":
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case "y":
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join("");
  }

  private sortPreviewImage(a: PreviewImageDetail, b: PreviewImageDetail) {
    if (a == null && b == null) {
      return 0;
    } else if (a == null) {
      return 1;
    } else if (b == null) {
      return -1;
    }

    if (a.inspectionId == null && b.inspectionId == null) {
      return 0;
    } else if (a.inspectionId == null || a.inspectionId > b.inspectionId) {
      return 1;
    } else if (b.inspectionId == null || a.inspectionId < b.inspectionId) {
      return -1;
    }

    if (a.subjectLocationId == null && b.subjectLocationId == null) {
      return 0;
    } else if (a.subjectLocationId == null) {
      return 1;
    } else if (b.subjectLocationId == null) {
      return -1;
    }

    return a.subjectLocationId < b.subjectLocationId ? -1 : 1;
  }

  private genInspectionImage(schema: InspectionImageSchema) {
    const result = new InspectionImage();
    result.id = schema.id;
    result.inspectionId = schema.inspectionId;
    result.subjectLocationId = schema.subjectLocationId;
    result.mimeType = schema.mimeType;
    result.datetime = schema.datetime; // 画像の撮影日
    result.fileName = schema.fileName;

    result.metadata = JSON.parse(schema.metadata) as Metadata;
    result.comment = schema.comment;
    result.isDataPlate = schema.isDataPlate;
    result.usedImageMode = schema.usedImageMode;
    result.hasModifiedImage = schema.modifiedItems !== null;
    result.hasMeasurementImage = schema.measurementItems !== null;
    result.status = schema.status;
    result.schemaVersion = schema.schemaVersion;
    result.orientation = schema.usedImageMode === 0 ? schema.orientation : 1;
    result.sortOrder = schema.sortOrder;
    result.modifiedWidth = schema.modifiedWidth;
    result.modifiedHeight = schema.modifiedHeight;
    result.isMeasurementEnabled = schema.isMeasurementEnabled;

    return result;
  }

  public calcSummaryPage(
    inspectionImages: PreviewImageDetail[],
    reportLocations: ReportLocation[],
    subjectModel: SubjectModel,
    locList: SubjectLocation[],
    summaryPage: TemplatePageSection
  ) {
    const inspectionSummary = [];
    const subtotal = {};
    // const summaryPage = this.originTemplate.template.bodies.find(body => body.inspectionSummary != null);
    const summaryData = new Array<Array<TableData>>();
    let summaryLocations = [];

    if (summaryPage != null && summaryPage.inspectionSummary.isEmptyFolderDisplayed) {
      locList.forEach((loc) => {
        const targetLoc = reportLocations.find((reportLoc) => reportLoc.identifier === loc.identifier);
        if (targetLoc) {
          summaryLocations.push(targetLoc);
        } else {
          summaryLocations.push(new ReportLocation(loc.identifier, []));
        }
      });
    } else {
      summaryLocations = reportLocations;
    }

    if (summaryPage != null && inspectionImages.some((_) => !_.isDataPlate)) {
      const summaryPageBreakRow = summaryPage.inspectionSummary.defaultPageBreakRowCount;
      const dataMode = summaryPage.inspectionSummary.table.dataMode;

      const summaryDataGenerator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
      const list = summaryDataGenerator.generateReportData(summaryLocations, subjectModel, dataMode);
      summaryData.push(list.filter((data) => data.images.length > 0));

      summaryData.forEach((data) => {
        const summaryPageSeparator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
        const pages = summaryPageSeparator.separatePageReportData(data, summaryPageBreakRow, "Summary");
        pages.forEach((page) => inspectionSummary.push(page));
        const groupBy = <K extends PropertyKey, V>(array: readonly V[], getKey: (cur: V, idx: number, src: readonly V[]) => K) =>
          array.reduce(
            (obj, cur, idx, src) => {
              const key = getKey(cur, idx, src);
              if (!obj[key]) {
                obj[key] = [];
              }
              obj[key].push(cur);
              return obj;
            },
            {} as Partial<Record<K, V[]>>
          );

        if (summaryPage.inspectionSummary.subTotal !== null) {
          pages.forEach((page) => {
            const group = groupBy(page, (d) => d.mark);
            const groupList = Object.entries(group);
            groupList.forEach((g) => {
              const key = Number(g[0]);
              const count = g[1].length;
              if (subtotal[key] !== null && subtotal[key] !== undefined) {
                subtotal[key] += count;
              } else {
                subtotal[key] = count;
              }
            });
          });
        }
      });
    }

    return inspectionSummary;
  }

  private genCacheParam() {
    return "?var=" + performance.now();
  }

  private isImage(imageType: string): boolean {
    const imageTypes = [
      MEASUREMENT_IMAGE_TYPE.ANYIMAGE_IMAGE,
      MEASUREMENT_IMAGE_TYPE.IV7_IMAGE,
      MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE,
      MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE,
      MEASUREMENT_IMAGE_TYPE.THESEUS_IMAGE,
      MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE
    ];
    return imageTypes.includes(imageType);
  }
}
